/**
 * Ticket Template Management Page
 *
 * Admin page for managing ticket templates
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTicketTemplates,
  createTicketTemplate,
  updateTicketTemplate,
  deleteTicketTemplate,
  toggleTemplateActive,
} from '@/services/tickets';
import type {
  TicketTemplate,
  CreateTemplateData,
  UpdateTemplateData,
  TicketCategory,
} from '@/types';

export function TicketTemplateManagementPage() {
  const { t } = useTranslation('admin');

  const categoryLabels: Record<TicketCategory, string> = {
    technical: t('ticket_management.category.technical'),
    billing: t('ticket_management.category.billing'),
    feature_request: t('ticket_management.category.feature_request'),
    bug_report: t('ticket_management.category.bug_report'),
    account: t('ticket_management.category.account'),
    other: t('ticket_management.category.other'),
  };
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TicketTemplate | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<CreateTemplateData>({
    name: '',
    category: 'other',
    subject: '',
    content: '',
    is_active: true,
  });

  const [filters, setFilters] = useState({
    category: '',
    is_active: '',
    search: '',
  });

  useEffect(() => {
    loadTemplates();
  }, [filters]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getTicketTemplates({
        category: filters.category || undefined,
        is_active: filters.is_active ? filters.is_active === 'true' : undefined,
        search: filters.search || undefined,
      });

      setTemplates(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_templates.messages.load_error'));
      console.error('Error loading templates:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (template?: TicketTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        name: template.name,
        category: template.category,
        subject: template.subject,
        content: template.content,
        is_active: template.is_active,
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        name: '',
        category: 'other',
        subject: '',
        content: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      category: 'other',
      subject: '',
      content: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      setError(null);

      if (editingTemplate) {
        await updateTicketTemplate(editingTemplate.id, formData);
      } else {
        await createTicketTemplate(formData);
      }

      handleCloseModal();
      await loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_templates.messages.save_error'));
      console.error('Error saving template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (template: TicketTemplate) => {
    try {
      await toggleTemplateActive(template.id);
      await loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_templates.messages.toggle_error'));
      console.error('Error toggling template:', err);
    }
  };

  const handleDelete = async (template: TicketTemplate) => {
    if (!confirm(`${t('ticket_templates.confirmations.delete')} "${template.name}"?`)) {
      return;
    }

    try {
      await deleteTicketTemplate(template.id);
      await loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_templates.messages.delete_error'));
      console.error('Error deleting template:', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('ticket_templates.page_title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('ticket_templates.page_subtitle')}
          </p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary">
          {t('ticket_templates.create_button')}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              id="template-search"
              type="text"
              placeholder={t('ticket_templates.filters.search_placeholder')}
              className="input w-full"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          <div className="w-48">
            <select
              id="category-filter"
              className="input w-full"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <option value="">{t('ticket_templates.filters.all_categories')}</option>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="w-40">
            <select
              id="status-filter"
              className="input w-full"
              value={filters.is_active}
              onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
            >
              <option value="">{t('ticket_templates.filters.all_status')}</option>
              <option value="true">{t('ticket_templates.filters.active')}</option>
              <option value="false">{t('ticket_templates.filters.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Template List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : templates.length === 0 ? (
          <div className="col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500 mb-4">{t('ticket_templates.messages.no_templates')}</p>
            <button onClick={() => handleOpenModal()} className="btn btn-primary">
              {t('ticket_templates.create_first_button')}
            </button>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {template.is_active ? t('ticket_templates.status.active') : t('ticket_templates.status.inactive')}
                    </span>
                  </div>
                  <p className="text-sm text-indigo-600 font-medium">{categoryLabels[template.category]}</p>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('ticket_templates.card.subject_label')}</p>
                  <p className="text-sm text-gray-900">{template.subject}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{t('ticket_templates.card.content_preview_label')}</p>
                  <p className="text-sm text-gray-600 line-clamp-2">{template.content}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleOpenModal(template)}
                  className="btn btn-secondary text-sm flex-1"
                >
                  {t('ticket_templates.card.edit_button')}
                </button>
                <button
                  onClick={() => handleToggleActive(template)}
                  className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors flex-1 ${
                    template.is_active
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {template.is_active ? t('ticket_templates.card.deactivate_button') : t('ticket_templates.card.activate_button')}
                </button>
                <button
                  onClick={() => handleDelete(template)}
                  className="btn btn-danger text-sm px-4"
                >
                  {t('ticket_templates.card.delete_button')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingTemplate ? t('ticket_templates.modal.edit_title') : t('ticket_templates.modal.create_title')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">
                  {t('ticket_templates.modal.name_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('ticket_templates.modal.name_placeholder')}
                  required
                />
              </div>

              <div>
                <label className="label">
                  {t('ticket_templates.modal.category_label')} <span className="text-red-500">*</span>
                </label>
                <select
                  className="input"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value as TicketCategory })
                  }
                  required
                >
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">
                  {t('ticket_templates.modal.subject_label')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder={t('ticket_templates.modal.subject_placeholder')}
                  required
                />
              </div>

              <div>
                <label className="label">
                  {t('ticket_templates.modal.content_label')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="input"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={t('ticket_templates.modal.content_placeholder')}
                  rows={8}
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  {t('ticket_templates.modal.is_active_label')}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                  disabled={isSaving}
                >
                  {t('ticket_templates.modal.cancel_button')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? t('ticket_templates.modal.saving') : editingTemplate ? t('ticket_templates.modal.update_button') : t('ticket_templates.modal.create_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
